import {
  AGENT_GRADES,
  AGENT_INDUSTRIES,
  AGENT_LANGUAGES,
  AGENT_USE_CASES,
  DEMO_EMBED_TYPES,
  NUMBER_TYPES,
  resolveAppPath,
  type AgentCreatePrefill,
  type DemoCreatePrefill,
  type NumberAttachPrefill,
} from "@/symphony/contract";

export interface ConfirmRequest {
  title: string;
  description: string;
  confirmLabel: string;
  fields: Array<{ label: string; value: string }>;
}

export interface SymphonyHost {
  navigate: (path: string) => void;
  confirm: (request: ConfirmRequest) => Promise<boolean>;
  openAgentCreate: (prefill: AgentCreatePrefill) => void;
  openDemoCreate: (prefill: DemoCreatePrefill) => void;
  openNumberAttach: (prefill: NumberAttachPrefill) => void;
  isAdmin: () => Promise<boolean>;
}

export type ActionResult =
  | { ok: true; submitted: false; opened: string }
  | { ok: false; submitted: false; error: string };

export interface SymphonyActionSchema {
  name: string;
  description: string;
  effect: "navigate" | "prefill";
  writes: false;
  requiresConfirmation: boolean;
  parameters: Record<string, unknown>;
}

const text = { type: "string" };
const object = (properties: Record<string, unknown>, required: string[] = []) => ({
  type: "object",
  additionalProperties: false,
  properties: {
    ...properties,
    submit: { type: "boolean", description: "Ignored. Speak never auto-submits." },
    save: { type: "boolean", description: "Ignored. Speak never auto-submits." },
    autoSubmit: { type: "boolean", description: "Ignored. Speak never auto-submits." },
  },
  required,
});

export const SYMPHONY_ACTIONS: SymphonyActionSchema[] = [
  {
    name: "app.navigate",
    description: "Open an in-app Speak route. Does not change data.",
    effect: "navigate",
    writes: false,
    requiresConfirmation: false,
    parameters: object({ path: { ...text, description: "Absolute in-app path, for example /agents or /campaigns/new." } }, ["path"]),
  },
  {
    name: "agent.openCreate",
    description: "Open the create-agent form with optional prefill. The user must press Create Agent. Nothing is saved by this tool.",
    effect: "prefill",
    writes: false,
    requiresConfirmation: true,
    parameters: object({
      name: text,
      description: text,
      industry: { type: "string", enum: [...AGENT_INDUSTRIES] },
      useCase: { type: "string", enum: [...AGENT_USE_CASES] },
      systemPrompt: text,
      firstMessage: text,
      language: { type: "string", enum: [...AGENT_LANGUAGES] },
      grade: { type: "string", enum: [...AGENT_GRADES] },
    }),
  },
  {
    name: "demoPage.create",
    description: "Open the admin demo-page form with optional prefill. Admins only. The user must press save. Nothing is created by this tool.",
    effect: "prefill",
    writes: false,
    requiresConfirmation: true,
    parameters: object({
      customerName: text,
      slug: text,
      embedType: { type: "string", enum: [...DEMO_EMBED_TYPES] },
      contactEmail: text,
      contactPhone: text,
      demoPhoneDisplay: text,
      widgetSnippet: text,
    }),
  },
  {
    name: "number.attach",
    description: "Open the phone-number claim or order flow. Does not claim, buy, or assign a number.",
    effect: "prefill",
    writes: false,
    requiresConfirmation: true,
    parameters: object({
      countryCode: { type: "string", description: "ISO 3166-1 alpha-2 country code, for example GB." },
      numberType: { type: "string", enum: [...NUMBER_TYPES] },
    }),
  },
  {
    name: "help.openDocs",
    description: "Open the in-app API docs.",
    effect: "navigate",
    writes: false,
    requiresConfirmation: false,
    parameters: object({
      section: { type: "string", description: "Optional hash on /api-docs. Letters, numbers, and hyphens only." },
    }),
  },
];

export const ACCOUNT_CONTEXT_TOOL: SymphonyActionSchema = {
  name: "account.context",
  description: "Read the signed-in user's agents, numbers, plan, call-credit balance, and demo pages. Speak about money in the account currency. Billing is read-only.",
  effect: "navigate",
  writes: false,
  requiresConfirmation: false,
  parameters: { type: "object", additionalProperties: false, properties: {} },
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function cleanText(value: unknown, max: number): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.replace(/\0/g, "").trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, max);
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === "string" && (allowed as readonly string[]).includes(value) ? value as T : undefined;
}

function attemptedSubmit(args: Record<string, unknown>): boolean {
  return args.submit === true || args.save === true || args.autoSubmit === true;
}

function fieldList(entries: Array<[string, string | undefined]>): Array<{ label: string; value: string }> {
  return entries
    .filter((entry): entry is [string, string] => Boolean(entry[1]))
    .map(([label, value]) => ({ label, value }));
}

function slugify(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 80);
}

function parseAgentPrefill(args: Record<string, unknown>): AgentCreatePrefill {
  return {
    name: cleanText(args.name, 120),
    description: cleanText(args.description, 500),
    industry: oneOf(args.industry, AGENT_INDUSTRIES),
    useCase: oneOf(args.useCase, AGENT_USE_CASES),
    systemPrompt: cleanText(args.systemPrompt, 8000),
    firstMessage: cleanText(args.firstMessage, 500),
    language: oneOf(args.language, AGENT_LANGUAGES),
    grade: oneOf(args.grade, AGENT_GRADES),
  };
}

function parseDemoPrefill(args: Record<string, unknown>): DemoCreatePrefill {
  const customerName = cleanText(args.customerName, 160);
  const rawSlug = cleanText(args.slug, 80);
  const email = cleanText(args.contactEmail, 160);
  return {
    customerName,
    slug: rawSlug ? slugify(rawSlug) : customerName ? slugify(customerName) : undefined,
    embedType: oneOf(args.embedType, DEMO_EMBED_TYPES),
    contactEmail: email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : undefined,
    contactPhone: cleanText(args.contactPhone, 40),
    demoPhoneDisplay: cleanText(args.demoPhoneDisplay, 40),
    widgetSnippet: cleanText(args.widgetSnippet, 2000),
  };
}

function parseNumberPrefill(args: Record<string, unknown>): NumberAttachPrefill {
  const country = cleanText(args.countryCode, 2)?.toUpperCase();
  return {
    countryCode: country && /^[A-Z]{2}$/.test(country) ? country : undefined,
    numberType: oneOf(args.numberType, NUMBER_TYPES),
  };
}

function confirmCopy(title: string, description: string, fields: Array<{ label: string; value: string }>, submitAsked: boolean): ConfirmRequest {
  return {
    title,
    description: submitAsked
      ? `${description} Symphony asked to save or submit. Speak will only open the form. Nothing is saved until you press the button yourself.`
      : description,
    confirmLabel: "Open form",
    fields,
  };
}

export async function runSymphonyAction(name: string, args: unknown, host: SymphonyHost): Promise<ActionResult> {
  const action = SYMPHONY_ACTIONS.find((item) => item.name === name);
  if (!action || action.writes !== false) {
    return { ok: false, submitted: false, error: "unknown_action" };
  }
  const record = isRecord(args) ? args : {};
  const submitAsked = attemptedSubmit(record);

  if (name === "app.navigate") {
    const path = typeof record.path === "string" ? resolveAppPath(record.path) : null;
    if (!path) return { ok: false, submitted: false, error: "path_not_allowed" };
    host.navigate(path);
    return { ok: true, submitted: false, opened: path };
  }

  if (name === "help.openDocs") {
    const section = cleanText(record.section, 64);
    const hash = section && /^[a-z0-9-]+$/i.test(section) ? `#${section}` : "";
    const path = `/api-docs${hash}`;
    host.navigate(path);
    return { ok: true, submitted: false, opened: path };
  }

  if (name === "demoPage.create") {
    const admin = await host.isAdmin();
    if (!admin) return { ok: false, submitted: false, error: "admin_required" };
    const prefill = parseDemoPrefill(record);
    const accepted = await host.confirm(confirmCopy(
      "Open a demo page draft?",
      "This opens the demo page form with the details below. It does not create the page.",
      fieldList([
        ["Customer", prefill.customerName],
        ["Slug", prefill.slug],
        ["Embed", prefill.embedType],
        ["Email", prefill.contactEmail],
      ]),
      submitAsked,
    ));
    if (!accepted) return { ok: false, submitted: false, error: "cancelled" };
    host.openDemoCreate(prefill);
    host.navigate("/admin?tab=demo-pages");
    return { ok: true, submitted: false, opened: "/admin?tab=demo-pages" };
  }

  if (name === "agent.openCreate") {
    const prefill = parseAgentPrefill(record);
    const accepted = await host.confirm(confirmCopy(
      "Open the new agent form?",
      "This fills the agent form. It does not create the agent.",
      fieldList([
        ["Name", prefill.name],
        ["Industry", prefill.industry],
        ["Use case", prefill.useCase],
        ["Language", prefill.language],
      ]),
      submitAsked,
    ));
    if (!accepted) return { ok: false, submitted: false, error: "cancelled" };
    host.openAgentCreate(prefill);
    host.navigate("/agents");
    return { ok: true, submitted: false, opened: "/agents" };
  }

  if (name === "number.attach") {
    const prefill = parseNumberPrefill(record);
    const accepted = await host.confirm(confirmCopy(
      "Open the number flow?",
      "This opens the claim or order dialog. It does not claim, buy, or attach a number.",
      fieldList([
        ["Country", prefill.countryCode],
        ["Type", prefill.numberType],
      ]),
      submitAsked,
    ));
    if (!accepted) return { ok: false, submitted: false, error: "cancelled" };
    host.openNumberAttach(prefill);
    host.navigate("/settings?tab=phone-numbers");
    return { ok: true, submitted: false, opened: "/settings?tab=phone-numbers" };
  }

  return { ok: false, submitted: false, error: "unknown_action" };
}
